import { getUserHash } from 'api/v2/users/authentication';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useQuery from 'hooks/react-query/useQuery';

import useIntercomStore from 'store/intercom.store';
import useGetLoggedInUserData from './Authentication/useGetLoggedInUserData';
import useFeatureLock from './Subscriptions/useFeatureLock';
import { SubscriptionLockKeysEnum } from 'types/subscriptions.types';
import { talkToTaxCoachLockContent } from 'components/organisms/PaywallLockV2/locks.content';

export type IUseIntercom = {
  boot: (settings: Intercom_.IntercomSettings) => void;
  update: (settings: Intercom_.IntercomSettings | undefined) => void;
  show: () => void;
  showIntercom: (options?: any) => void;
  getVisitorId: () => string;
  shutdown: () => void;
  onUnreadCountChange: (cb: (unreadCount: number) => void) => void;
  showNewMessage: (message?: string) => void;
};

export const getIntercom = (): IUseIntercom => {
  const boot: IUseIntercom['boot'] = (settings) => Intercom('boot', settings);
  const update: IUseIntercom['update'] = (
    settings: Intercom_.IntercomSettings | undefined,
  ) => Intercom('update', settings);
  const show = () => Intercom('show');
  const getVisitorId = () => Intercom('getVisitorId');
  const shutdown = () => Intercom('shutdown');
  const onUnreadCountChange: IUseIntercom['onUnreadCountChange'] = (cb) =>
    Intercom('onUnreadCountChange', cb);
  const showNewMessage = (message?: string) =>
    Intercom('showNewMessage', message);

  return {
    boot,
    update,
    show,
    showIntercom: show,
    getVisitorId,
    shutdown,
    onUnreadCountChange,
    showNewMessage,
  };
};

const onUnreadCountChange = (count: number): void => {
  useIntercomStore.setState({ unreadCount: count });
};

export const useInitializeIntercom = (): void => {
  const { data: user } = useGetLoggedInUserData(); // get customer or expert
  const { data: userHash } = useQuery(
    ['useInitializeIntercom', user?._id],
    getUserHash,
    {
      enabled: !!user?._id,
    },
  );
  const {
    i18n: { language },
  } = useTranslation();

  useEffect(() => {
    const intercom = getIntercom();
    if (userHash) {
      intercom.boot({
        app_id: import.meta.env.VITE_APP_INTERCOM_APP_ID,
        user_hash: userHash as unknown as string,
        hide_default_launcher: true,
        email: user?.email,
        name: `${user?.firstName || ''} ${user?.lastName || ''}`,
        user_id: user?._id,
        language_override: language,
      });
    } else {
      intercom.boot({
        app_id: import.meta.env.VITE_APP_INTERCOM_APP_ID,
        hide_default_launcher: true,
        language_override: language,
      });
    }
    intercom.onUnreadCountChange(onUnreadCountChange);
  }, [
    language,
    user?._id,
    user?.email,
    user?.firstName,
    user?.lastName,
    userHash,
  ]);

  useEffect(() => {
    if ((window as any).shouldOpenIntercomInitially) {
      (window as any).shouldOpenIntercomInitially = false;
      getIntercom().show();
    }
  }, []);
};

export const useIntercom = (): IUseIntercom => {
  const { withPaywallLock } = useFeatureLock();

  const intercom = getIntercom();

  const showIntercom = useCallback(
    (
      options: {
        ignoreLocking?: boolean;
      } = {},
    ) => {
      if (options?.ignoreLocking) return intercom.show();

      return withPaywallLock(
        () => intercom.show(),
        SubscriptionLockKeysEnum.talkToTaxCoach,
        talkToTaxCoachLockContent,
      );
    },
    [withPaywallLock, intercom],
  );

  const showNewMessage = useCallback(
    (
      message?: string,
      options?: {
        ignoreLocking?: boolean;
      },
    ) => {
      if (options?.ignoreLocking) return intercom.showNewMessage(message);

      return withPaywallLock(
        () => intercom.showNewMessage(message),
        SubscriptionLockKeysEnum.talkToTaxCoach,
        talkToTaxCoachLockContent,
      );
    },
    [withPaywallLock, intercom],
  );

  return {
    ...intercom,
    showIntercom,
    showNewMessage,
  };
};

export default getIntercom;
